import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CalendarDays, HandCoins, Wallet } from 'lucide-react';
import { AGEING_BUCKETS, type PayableSupplierPosition } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { StatCard } from '@/components/ui/StatCard';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { apiErrorMessage } from '@/services/apiClient';
import {
  usePayableSuppliersQuery,
  usePayablesAgeingQuery,
  usePayablesSummaryQuery,
} from '../api/payables.api';

/**
 * What the business owes its suppliers, and how late it is.
 *
 * Every figure here comes from the server, scoped to the branches this user may reach. Nothing is
 * added up from a page of rows in the browser — a total that quietly means "the first twenty
 * invoices" is worse than no total, because it looks like an answer.
 *
 * The boundary this page exists to make obvious: a PINV is an obligation the business has
 * recognised, an SPAY is money that has left. They are different documents with different
 * lifecycles, and no screen here lets one masquerade as the other.
 */
export function PayablesDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { branches } = useBranch();
  const { format } = useCurrency();

  // Its own filter, not the app's active branch: reading what is owed across every branch someone can reach is a normal finance question, and switching the whole app to answer it is not.
  const [branchId, setBranchId] = useState('');

  const summary = usePayablesSummaryQuery({ branchId });
  const ageing = usePayablesAgeingQuery({ branchId });
  const suppliers = usePayableSuppliersQuery({ branchId });

  const supplierColumns: Column<PayableSupplierPosition>[] = [
    {
      key: 'supplier',
      header: t('payables.fields.supplier'),
      primary: true,
      render: (row) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium" dir="auto">
            {row.supplierName}
          </span>
          <span className="text-muted text-xs">
            {t('payables.invoiceCount', { count: row.invoiceCount })}
          </span>
        </span>
      ),
    },
    {
      key: 'outstanding',
      header: t('payables.fields.outstanding'),
      align: 'end',
      render: (row) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(row.outstandingMinor)}
        </span>
      ),
    },
    {
      key: 'overdue',
      header: t('payables.fields.overdue'),
      align: 'end',
      render: (row) =>
        row.overdueMinor > 0 ? (
          <span className="flex flex-col items-end">
            <span className="font-semibold text-red-700" dir="ltr">
              {format(row.overdueMinor)}
            </span>
            <span className="text-muted text-xs">
              {t('payables.overdueCount', { count: row.overdueCount })}
            </span>
          </span>
        ) : (
          <span className="text-muted text-xs">{t('payables.nothingLate')}</span>
        ),
    },
    {
      key: 'oldest',
      header: t('payables.fields.oldestDue'),
      render: (row) => (
        <span className="text-muted text-sm" dir="ltr">
          {row.oldestDueDate ?? '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('payables.openSupplierPayables', { supplier: row.supplierName })}
          onClick={() =>
            void navigate(
              `/finance/payables/invoices?supplierId=${row.supplierId}${
                branchId ? `&branchId=${branchId}` : ''
              }`,
            )
          }
        >
          {t('payables.viewInvoices')}
        </Button>
      ),
    },
  ];

  const totals = ageing.data?.totals;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('payables.title')}
        subtitle={t('payables.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => void navigate('/finance/payables/payments')}>
              {t('payables.payments.title')}
            </Button>
            <Button onClick={() => void navigate('/finance/payables/invoices')}>
              {t('payables.invoices.title')}
            </Button>
          </div>
        }
      />

      {branches.length > 1 ? (
        <div className="max-w-xs">
          <Select
            options={[
              { value: '', label: t('payables.filters.allBranches') },
              ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
            ]}
            value={branchId}
            aria-label={t('payables.fields.branch')}
            onChange={(event) => setBranchId(event.target.value)}
          />
        </div>
      ) : null}

      {summary.isError ? (
        <Alert tone="error">{apiErrorMessage(summary.error) ?? t('payables.loadError')}</Alert>
      ) : null}

      {/* A landmark of its own: the four figures are the answer to "how bad is it", and a screen reader should be able to jump to them rather than walk the page. */}
      <section
        aria-label={t('payables.summaryRegion')}
        className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        <StatCard
          icon={Wallet}
          label={t('payables.stats.outstanding')}
          value={format(summary.data?.outstandingMinor ?? 0)}
          hint={t('payables.stats.outstandingHint', { count: summary.data?.invoiceCount ?? 0 })}
          isLoading={summary.isPending}
        />
        <StatCard
          icon={CalendarDays}
          label={t('payables.stats.dueToday')}
          value={format(summary.data?.dueTodayMinor ?? 0)}
          hint={t('payables.stats.dueTodayHint', { count: summary.data?.dueTodayCount ?? 0 })}
          tone="warning"
          isLoading={summary.isPending}
        />
        <StatCard
          icon={AlertTriangle}
          label={t('payables.stats.overdue')}
          value={format(summary.data?.overdueMinor ?? 0)}
          hint={t('payables.stats.overdueHint', { count: summary.data?.overdueCount ?? 0 })}
          tone="danger"
          isLoading={summary.isPending}
        />
        <StatCard
          icon={CalendarClock}
          label={t('payables.stats.upcoming')}
          value={format(summary.data?.upcomingMinor ?? 0)}
          hint={t('payables.stats.upcomingHint', { count: summary.data?.upcomingCount ?? 0 })}
          tone="neutral"
          isLoading={summary.isPending}
        />
      </section>

      <Card>
        <CardHeader
          title={t('payables.ageingTitle')}
          subtitle={
            ageing.data ? t('payables.asOf', { date: ageing.data.asOf }) : t('payables.ageingHint')
          }
        />
        {/* The receivables scale, not a second one: the same buckets mean the same thing whichever way the money is going. */}
        <dl
          role="group"
          aria-label={t('payables.ageingRegion')}
          className="bg-line grid gap-px sm:grid-cols-5"
        >
          {AGEING_BUCKETS.map((bucket) => (
            <div key={bucket} className="bg-surface flex flex-col gap-1 p-4">
              <dt className="text-muted text-xs font-medium tracking-wide uppercase">
                {t(`payables.buckets.${bucket}`)}
              </dt>
              <dd
                className={
                  bucket === 'D90_PLUS' && (totals?.[bucket] ?? 0) > 0
                    ? 'font-semibold text-red-700'
                    : 'text-ink font-semibold'
                }
                dir="ltr"
              >
                {format(totals?.[bucket] ?? 0)}
              </dd>
            </div>
          ))}
        </dl>
        <p className="text-muted border-line border-t px-4 py-3 text-xs">
          {t('payables.dueTodayIsNotOverdue')}
        </p>
      </Card>

      <Card>
        <CardHeader title={t('payables.bySupplierTitle')} subtitle={t('payables.bySupplierHint')} />
        <DataTable
          caption={t('payables.bySupplierCaption')}
          columns={supplierColumns}
          rows={suppliers.data}
          rowKey={(row) => row.supplierId}
          isLoading={suppliers.isPending}
          isError={suppliers.isError}
          errorMessage={apiErrorMessage(suppliers.error) ?? t('payables.loadError')}
          onRetry={() => void suppliers.refetch()}
          emptyIcon={HandCoins}
          emptyTitle={t('payables.nothingOwed')}
          emptyDescription={t('payables.nothingOwedHint')}
        />
      </Card>
    </div>
  );
}
